import React, { useEffect, useRef, useState } from 'react';
import DataGrid from 'devextreme-react/data-grid';
import FilterRow from 'components/common/DataGridDevEx/FilterRow';
import filterRow from './RecallParkedSaleDialogContent.filterRow';
import { Popup } from 'devextreme-react/popup';
import styles from './RecallParkedSaleDialogContent.styles';
import { withStyles } from '@material-ui/core';
import { deleteParkSale, fetchInitialRetrieveParkedSales } from 'api/Worksale/worksale';
import { IFilters, IRecallParkedSaleDialogContentHandle, IRecallParkedSaleDialogContentProps } from './RecallParkedSaleDialog.properties';
import { IMessageModelFacade, IParkedSaleFilter } from 'api/swaggerTypes';
import InfoIcon from '@markinson/uicomponents-v2/SvgIcons/Info';
import { isNull } from 'utils/utils';
import classNames from 'classnames';
import { columns, DATAGRIDCONSTANTS } from 'components/Worksale/RecallParkedSaleDialog/RecallParkedSaleDialog.constants';
import { useRetriveParkedSale } from 'api/Worksale/worksaleLatest';
import { ENTER_KEY, ESCAPE, TAB } from 'utils/constants';
import { useGridKeyboardNavigation } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { useMemoizedKeyHandlers } from 'utils/hooks';
import { HotKeys } from 'react-hotkeys';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';

const DELAY = 10;

const RecallParkedSaleDialogContent = (props: IRecallParkedSaleDialogContentProps, ref: React.Ref<IRecallParkedSaleDialogContentHandle>) => {
    const { classes, worksaleId, recallButtonRef, selectedRow, setSelectedRow, setGridLength, onCancel, onRecall, onGridTabOut, onFilterButtonShiftTab, changeConfirmationDialog, recallParkedSaleApi } = props;
    const dataGridRef = useRef<DataGrid>();

    const [loading, setLoading] = useState<boolean>(false);
    const [notesOpen, setNotesOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState<IFilters>();
    const [filtersValuesSchema, setFiltersValuesSchema] = React.useState<any>({});
    const [note, setNote] = useState<string>('');

    const { isLoading, data, mutateAsync } = useRetriveParkedSale();

    const filterButtonRef = React.useRef<HTMLButtonElement>();
    const { onFocusedCellChanging } = useGridKeyboardNavigation();
    const [defaultFirstRowSelection, setDefaultFirstRowSelection] = React.useState<boolean>(false);

    React.useEffect(
        () => {
            setGridLength(dataGridRef.current?.instance?.getDataSource()?.items().length);
        },
        [dataGridRef.current?.instance?.getDataSource()?.items().length]);

    const recallParkedSaleInquiry = React.useCallback(
        () => {
            setLoading(true);
            recallParkedSaleApi({
                ParkedSaleId: selectedRow?.ParkedSaleId,
                WorkSaleId: worksaleId
            }).then((response) => {
                if (response.Status && isNull(response.Messages)) {
                    onCancel();
                    showSnackBar({ variant: 'success', message: 'Parked Sale recalled successfully.' });
                    onRecall();
                } else {
                    if (!isNull(response.Messages)) {
                        showMessages(response.Messages);
                    }
                    showSnackBar({ variant: 'error', message: 'Failed to recall parked sale.' });
                }

            }).catch((err) => console.warn(err))
                .finally(() => {
                    setLoading(false);
                });
        },
        [selectedRow, worksaleId]
    );

    function showMessages(messages: IMessageModelFacade[]): void {
        const ExceptionDetails = messages.reduce((p, c) => p ? `${p}\n- ${c.MessageDetail}` : `- ${c.MessageDetail}`, '');
        showSnackBar({ variant: 'error', message: 'error', time: -1, detail: { ExceptionDetails } });
    }

    React.useImperativeHandle(
        ref,
        () => ({
            focusFilterButton,
            focusGrid(): void {
                focusGrid();
            },
            recall: recallParkedSaleInquiry,
        }),
        [selectedRow, recallParkedSaleInquiry]
    );

    React.useEffect(
        () => {
            setDefaultFirstRowSelection(true);
        },
        []
    );

    useEffect(
        () => {
            setLoading(true);
            fetchInitialRetrieveParkedSales()
                .then((response) => {
                    const { InitialisedParkedSales } = response;
                    if (InitialisedParkedSales) {
                        const _filters = {
                            CustomerId: InitialisedParkedSales.inlineObject.CustomerId,
                            Username: InitialisedParkedSales.inlineObject.Username,
                            UserID: InitialisedParkedSales.inlineObject.UserID,
                            SalesEntity: InitialisedParkedSales.inlineObject.Entity,
                            ShowParkedSales: InitialisedParkedSales.inlineObject.ShowParkedSales,
                        };

                        setFilters(_filters);
                        setFiltersValuesSchema(InitialisedParkedSales?.schema);
                        filterOnApply({ filters: _filters });
                    }
                }).catch((err) => console.warn(err))
                .finally(() => {
                    setLoading(false);
                });
        },
        []
    );

    useEffect(
        () => {
            if (loading || isLoading) {
                if (dataGridRef.current) {
                    dataGridRef.current.instance.beginCustomLoading('');
                }
            } else {
                dataGridRef.current.instance.endCustomLoading();
            }
        },
        [loading, isLoading]
    );

    const onEscape = React.useCallback(
        () => {
            if (onCancel) {
                onCancel();
            }
        },
        [onCancel]
    );

    const onEnter = React.useCallback(
        () => {
            if (recallButtonRef?.current &&
                dataGridRef.current.instance?.$element()?.[0]?.contains(document.activeElement)) {
                recallParkedSaleInquiry();
            }
        },
        [dataGridRef?.current, recallParkedSaleInquiry]
    );

    const handlers = useMemoizedKeyHandlers({
        Escape: onEscape,
        Enter: onEnter
    });

    const focusFilterButton = React.useCallback(
        () => {
            setTimeout(() => filterButtonRef?.current?.focus(), DELAY);
        },
        [filterButtonRef.current]
    );

    const handleKeyDown = React.useCallback(
        (e) => {

            const keyboardEvent = e.event as KeyboardEvent;

            if (keyboardEvent.keyCode === TAB && !keyboardEvent.shiftKey) {
                setTimeout(
                    () => {
                        onGridTabOut();
                    },
                    0
                );

                return;
            }

            if (keyboardEvent.keyCode === TAB && keyboardEvent.shiftKey) {
                setTimeout(
                    () => {
                        focusFilterButton();
                    },
                    0
                );

                return;
            }

            if (keyboardEvent.keyCode === ESCAPE) {
                onEscape();

                return;
            }

            if (keyboardEvent.keyCode === ENTER_KEY) {
                onEnter();

                return;
            }
        },
        [onEscape, onEnter, focusFilterButton]
    );

    const focusAndSelectFirstRowGrid = React.useCallback(
        () => {
            if (!dataGridRef.current) {
                return;
            }

            dataGridRef.current?.instance?.getScrollable()?.scrollTo(0);
            focusGrid();

            if (dataGridRef.current?.instance?.getDataSource()?.items()?.length > 0) {
                dataGridRef.current?.instance?.selectRowsByIndexes([0]);
            } else {
                focusFilterButton();
            }
            setDefaultFirstRowSelection(false);
        },
        [dataGridRef.current, setDefaultFirstRowSelection]
    );

    const focusGrid = () => {
        setTimeout(
            () => {
                dataGridRef.current?.instance.focus();
            },
            0);
    };

    const onContentReady = React.useCallback(
        () => {
            if (defaultFirstRowSelection) {
                focusAndSelectFirstRowGrid();
            }
        },
        [defaultFirstRowSelection, focusAndSelectFirstRowGrid]
    );

    const filterOnApply = async ({ filters: appliedFilters }: { formName?: string; filters: IFilters }) => {
        const _appliedFilters = { ...appliedFilters, CustomerID: appliedFilters.CustomerId };
        setFilters(appliedFilters);
        if (appliedFilters) {
            await mutateAsync(_appliedFilters as IParkedSaleFilter);
            setDefaultFirstRowSelection(true);
        }
    };

    const infoButtonOnClick = (rowData) => {
        setNote(rowData.data.Note);
        setNotesOpen(true);
    };

    const removeRow = (rowData) => {
        changeConfirmationDialog({
            open: true,
            title: 'Delete selected record',
            message: 'Are you sure you want to delete the Selected record?',
            okLabel: 'Delete',
            onCancel: () => null,
            onOk: async () => {
                const result = await deleteParkSale(rowData?.ParkedSaleId);
                if (result?.Status) {
                    await mutateAsync({ CustomerID: filters.CustomerID, UserID: filters.UserID, Username: filters.Username, SalesEntity: filters.SalesEntity, ShowParkedSales: filters.ShowParkedSales });
                }
            },
        });
    };

    const handleOnSelectionChanged = React.useCallback(
        (e) => {
            const selectedRowData = e.selectedRowsData?.[0] ?? {};
            setSelectedRow(selectedRowData);
        },
        [setSelectedRow]
    );

    const handleFilterButtonTab = React.useCallback(
        () => {
            if (dataGridRef.current?.instance?.getDataSource()?.items()?.length > 0) {
                dataGridRef.current?.instance.focus();
            } else {
                onGridTabOut();
            }
        },
        [dataGridRef.current]
    );

    return (
        <HotKeys handlers={handlers}>
            <div className={classes.recallParkedSaleContainer}>
                <FilterRow
                    filterButtonRef={filterButtonRef}
                    disableInitialApplyCall={true}
                    parameters={filterRow.parameters}
                    formName={filterRow.formName}
                    onApplyFilters={filterOnApply}
                    initialValues={{ ...filters }}
                    valuesSchema={filtersValuesSchema}
                    validate={filterRow.validate}
                    rowModelType={filterRow.rowModelType}
                    filterRef={props?.filterRef}
                    onFilterButtonTab={handleFilterButtonTab}
                    onFilterButtonShiftTab={onFilterButtonShiftTab} />
                <DynamicDataGrid
                    dataGridClassName={classes.grid}
                    columns={columns}
                    allowHeaderFilting={false}
                    allowUpdating={false}
                    onDelete={removeRow}
                    gridProps={{
                        dataSource: data?.ParkedSales ?? [],
                        dataGridRef: dataGridRef,
                        sorting: { mode: 'single' },
                        height: DATAGRIDCONSTANTS.GRID_HEIGHT,
                        onContentReady,
                        onSelectionChanged: handleOnSelectionChanged,
                        onKeyDown: handleKeyDown,
                        onFocusedCellChanging,
                        onCellPrepared: (e) => {
                            const UserIDHeader = 2;
                            const EnteredHeader = 4;
                            if (e.rowType === 'header' && e.columnIndex === UserIDHeader) {
                                e.cellElement.style.textAlign = 'center';
                            }
                            if (e.rowType === 'header' && e.columnIndex === EnteredHeader) {
                                e.cellElement.style.textAlign = 'left';
                            }
                        }
                    }}
                    actionsAtStart={
                        (rowData: any) => <InfoIcon className={classNames(classes.infoButton, !rowData?.data.Notes && classes.disabled)} onClick={() => (rowData.data.Notes) ? infoButtonOnClick(rowData) : undefined} />
                    }
                />
                <Popup
                    visible={notesOpen}
                    title={'Notes'}
                    width={'auto'}
                    height={'auto'}
                    hideOnOutsideClick
                    onVisibleChange={setNotesOpen}
                    contentRender={() => {
                        return (<p className={classes.popup}>
                            {note}
                        </p>);
                    }}
                />
            </div>
        </HotKeys>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(RecallParkedSaleDialogContent)));
